/**
 * When it is acceptable to swap the running build out from under a visitor.
 *
 * Applying an update means a reload, and a reload discards everything the page
 * holds that is not in the URL: the scroll position down a 22-plant grid, a
 * half-typed search, the open picture viewer. The update is not urgent — nothing
 * here is a security fix or a schema migration — so it waits for a moment where
 * losing all of that costs nothing.
 *
 * The alternative shape, a "new version available, reload?" banner, was not
 * built: it spends a visitor's attention on a decision that is not theirs to
 * make, on a content app where the two builds differ by a plant.
 */

export const IDLE_BEFORE_AUTO_UPDATE_MS = 30 * 1000;

export function resolveAutoUpdateDecision({
    isPageVisible = true,
    isUserIdle = false,
    hasBlockingOverlay = false,
} = {}) {
    // The full-screen picture viewer is a modal the visitor deliberately opened
    // (SPEC-006). It is component state rather than a URL, so a reload closes
    // it — and "the picture I was looking at disappeared" is a bug report, not
    // an update.
    if (hasBlockingOverlay) {
        return { canUpdate: false, deferredReason: 'blocking-overlay' };
    }

    // Backgrounded is the free moment: nothing is being read, and the visitor
    // comes back to the new build already loaded.
    if (!isPageVisible) {
        return { canUpdate: true, deferredReason: null };
    }

    if (!isUserIdle) {
        return { canUpdate: false, deferredReason: 'foreground-active' };
    }

    return { canUpdate: true, deferredReason: null };
}
